import React, { useEffect, useMemo, useRef, useState } from 'react';
import { PromoCodeSortColumn, Sort, SpotInterface, ValidatedDiscountCode } from 'types';
import { DiscountCodeFormInputs, useDiscountCode } from 'contexts/DiscountCodeContext';
import Button from 'components/Button/Button';
import AGModal, { ModalRef } from 'components/Modal/Modal';
import AGMessage from 'components/AGMessage/AGMessage';
import Loader from 'components/Loader/Loader';
import Toggle from 'components/AGForm/Inputs/Toggle/Toggle';
import SearchInput from 'components/AGForm/Inputs/SearchInput/SearchInput';
import ConfirmAction from 'components/ConfirmAction/ConfirmAction';
import DiscountCodeModalForm from '../DiscountCodeModalForm/DiscountCodeModalForm';
import DiscountCodeFlyer from '../DiscountCodeFlyer/DiscountCodeFlyer';
import PromoCodeTable from '../PromoCodeTable/PromoCodeTable';
import styles from './PromoCode.module.css';

type PromoCodesProps = {
    selectedSpotInfo: SpotInterface;
    showingUsableCodes: boolean;
    page: number;
    toggleShowingUsableCodes: () => void;
    handlePageChange: (pageNumber: string) => void;
    searchTerm: string;
    currentSearchTerm: string;
    setSearchTerm: (searchTerm: string) => void;
    sort: Sort<PromoCodeSortColumn> | null;
    onSortChange: (column: PromoCodeSortColumn) => void;
};

const MAX_PROMO_SUBSTRING_LENGTH = 12;

function PromoCodes({
    selectedSpotInfo: { name: spotName, timezone: spotTimezone, pk: spotPk },
    showingUsableCodes,
    page,
    toggleShowingUsableCodes,
    handlePageChange,
    searchTerm,
    currentSearchTerm,
    setSearchTerm,
    sort,
    onSortChange,
}: PromoCodesProps) {
    const [selectedPromoCode, setSelectedPromoCode] = useState<ValidatedDiscountCode | null>(null);

    const promoSubstring = useMemo(
        () =>
            selectedPromoCode && selectedPromoCode.code.length > MAX_PROMO_SUBSTRING_LENGTH
                ? `${selectedPromoCode.code.substring(0, MAX_PROMO_SUBSTRING_LENGTH)}...`
                : selectedPromoCode?.code,
        [selectedPromoCode]
    );

    const {
        discountCodes,
        errorMessage,
        loading,
        operationLoading,
        getDiscountCodes,
        addDiscountCode,
        deleteDiscountCode,
    } = useDiscountCode();

    const generateQrCodeModal = useRef<ModalRef>();
    const addDiscountCodeModal = useRef<ModalRef>();
    const deleteDiscountCodeModal = useRef<ModalRef>();

    const codeFilters = useMemo(
        () => ({ page, usableCodes: showingUsableCodes, sort, search: searchTerm }),
        [page, showingUsableCodes, sort, searchTerm]
    );

    useEffect(() => {
        getDiscountCodes(spotPk, codeFilters);
    }, [getDiscountCodes, spotPk, codeFilters]);

    function searchPromoCodes(event: React.ChangeEvent<HTMLInputElement>) {
        setSearchTerm(event.target.value);
    }

    function openAddDiscountCodeModal() {
        addDiscountCodeModal.current?.openModal();
    }

    function openDeleteCodeModal(promoCode: ValidatedDiscountCode) {
        setSelectedPromoCode(promoCode);
        deleteDiscountCodeModal.current?.openModal();
    }
    function closeDeleteCodeModal() {
        setSelectedPromoCode(null);
        deleteDiscountCodeModal.current?.closeModal();
    }

    function handleDeletePromoCode() {
        if (selectedPromoCode !== null) {
            deleteDiscountCode(spotPk, selectedPromoCode.pk, codeFilters);
            setSelectedPromoCode(null);
        }
        deleteDiscountCodeModal.current?.closeModal();
    }

    function openQrCodeModal(discountCode: ValidatedDiscountCode) {
        setSelectedPromoCode(discountCode);
        generateQrCodeModal.current?.openModal();
    }

    function handleAddDiscountCode(inputs: DiscountCodeFormInputs) {
        addDiscountCode(spotPk, inputs, codeFilters);
        addDiscountCodeModal.current?.closeModal();
    }

    return (
        <div>
            <div>
                {errorMessage && <AGMessage color="error">{errorMessage}</AGMessage>}
                <div>
                    {/* Header */}
                    <div className={styles.header}>
                        <div>
                            <Toggle checked={!showingUsableCodes} onChange={toggleShowingUsableCodes} />
                            <span className={styles.toggleLabel}>Show discount codes with 0 uses left</span>
                        </div>
                        <Button onClick={openAddDiscountCodeModal} size="sm">
                            + Add Discount Code
                        </Button>
                    </div>

                    {/* Search */}
                    <div className={styles.filters}>
                        <SearchInput
                            onChange={searchPromoCodes}
                            value={currentSearchTerm}
                            placeholder="Search by code or description"
                            className={styles.searchInput}
                            type="text"
                        />
                    </div>

                    {/* Table */}
                    {loading || !discountCodes ? (
                        <Loader />
                    ) : (
                        <PromoCodeTable
                            promoCodes={discountCodes}
                            handlePaginationChange={handlePageChange}
                            deletePromoCode={openDeleteCodeModal}
                            openQrCodeModal={openQrCodeModal}
                            spotTimezone={spotTimezone}
                            sortColumn={sort?.column ?? null}
                            sortDirection={sort?.direction ?? 'asc'}
                            onSortChange={onSortChange}
                        />
                    )}

                    {/* Delete modal */}
                    <AGModal
                        ref={deleteDiscountCodeModal}
                        title={`Are you sure you want to delete${promoSubstring ? ` ${promoSubstring}` : ''}?`}
                        onClose={() => setSelectedPromoCode(null)}
                    >
                        <ConfirmAction
                            onCancel={closeDeleteCodeModal}
                            onConfirm={handleDeletePromoCode}
                            action="Delete"
                            loading={operationLoading}
                        >
                            <p className={styles.deleteText}>
                                This code will be deleted immediately and drivers will no longer be able to use it.
                            </p>
                        </ConfirmAction>
                    </AGModal>

                    {/* QR Code modal */}
                    <AGModal ref={generateQrCodeModal} title="Download QR Code Flyer">
                        {selectedPromoCode && (
                            <DiscountCodeFlyer spotName={spotName} discountCode={selectedPromoCode} />
                        )}
                    </AGModal>

                    {/* Add code Modal */}
                    <AGModal ref={addDiscountCodeModal} title="Add Discount Code">
                        <DiscountCodeModalForm
                            loading={operationLoading}
                            addPromoCode={handleAddDiscountCode}
                            spotTimezone={spotTimezone}
                        />
                    </AGModal>
                </div>
            </div>
        </div>
    );
}

export default PromoCodes;
